import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { LineChartProps } from "@mui/x-charts/LineChart";
import { LineChart } from "@mui/x-charts/LineChart";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { keepPreviousData } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNumberFormat } from "@/components/NumberFormat/hooks/useNumberFormat";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import type {
  ContactsChartMode,
  ContactsRangePreset,
} from "@/pages/billing/v2/tabs/overview/contactsChartData";
import {
  buildContactsChartData,
  CONTACTS_RANGE_PRESETS,
  formatContactsChartDate,
  getContactsDateRange,
  getContactsTickInterval,
  shouldShowContactsMark,
  supportsMonthlyAverage,
} from "@/pages/billing/v2/tabs/overview/contactsChartData";
import { ContactsFreshness } from "@/pages/billing/v2/tabs/overview/ContactsFreshness";
import { ContactsInfoTooltip } from "@/pages/billing/v2/tabs/overview/ContactsInfoTooltip";

const styles = {
  refreshProgress: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
  },
  card: {
    position: "relative",
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  cardContent: {
    padding: "24px",
    "&:last-child": {
      paddingBottom: "24px",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "300px",
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
  },
  filters: {
    display: "flex",
    flexDirection: "column",
    alignItems: { xs: "flex-start", sm: "flex-end" },
    gap: "8px",
    marginLeft: { xs: "0px", sm: "auto" },
    maxWidth: "100%",
  },
  range: { fontVariantNumeric: "tabular-nums" },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const reportingDayFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Taipei",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const chartSlots = { legend: () => null };
const chartMargin = { right: 40 };
const compactNumberFormatOptions = { notation: "compact" } as const;
const valueNumberFormatOptions = {
  daily: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
  monthly: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
} satisfies Record<ContactsChartMode, Intl.NumberFormatOptions>;

export const UniqueContactsCard: React.FC = function UniqueContactsCard() {
  const { t } = useTranslation();
  const yAxisFormat = useNumberFormat({
    numberFormatLocale: "en",
    numberFormatOptions: compactNumberFormatOptions,
  });
  const [preset, setPreset] = useState<ContactsRangePreset>("7");
  const [longRangeMode, setLongRangeMode] =
    useState<ContactsChartMode>("daily");
  const canShowMonthly = supportsMonthlyAverage(preset);
  const mode = canShowMonthly ? longRangeMode : "daily";
  const valueFormat = useNumberFormat({
    numberFormatOptions: valueNumberFormatOptions[mode],
    nullishValue: "—",
  });
  const dateRange = getContactsDateRange(
    preset,
    reportingDayFormat.format(new Date()),
  );
  const rangeInputProps = useMemo(
    () => ({
      "aria-label": t("billing.uniqueContacts.dateRange", "Date range"),
    }),
    [t],
  );
  const presetLabels: Record<ContactsRangePreset, string> = {
    "7": t("billing.uniqueContacts.last7Days"),
    "30": t("billing.uniqueContacts.last30Days"),
    "3m": t("billing.uniqueContacts.last3Months", "Last 3 months"),
    "6m": t("billing.uniqueContacts.last6Months", "Last 6 months"),
    "9m": t("billing.uniqueContacts.last9Months", "Last 9 months"),
    "1y": t("billing.uniqueContacts.last1Year", "Last 1 year"),
  };

  const uniqueContactsQuery =
    interlude.uniqueContacts.useGetUniqueContactsOverview(
      {
        queries: {
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
        },
      },
      { placeholderData: keepPreviousData },
    );

  const handlePresetChange = useHandler<
    React.ComponentProps<typeof Select>["onChange"]
  >((event) => setPreset(event.target.value as ContactsRangePreset));
  const handleModeChange = useHandler<
    React.ComponentProps<typeof ToggleButtonGroup>["onChange"]
  >((_event, value: ContactsChartMode | null) => {
    if (value !== null) setLongRangeMode(value);
  });

  const chartData = useMemo(
    () =>
      buildContactsChartData(
        uniqueContactsQuery.data?.records ?? [],
        dateRange.reportingFrom,
        dateRange.reportingTo,
        mode,
        uniqueContactsQuery.data?.dataAvailableFrom ?? "",
      ),
    [
      uniqueContactsQuery.data,
      dateRange.reportingFrom,
      dateRange.reportingTo,
      mode,
    ],
  );
  const counts = useMemo(
    () => chartData.counts.filter((count) => count !== null),
    [chartData.counts],
  );

  const yAxisRange = useMemo(() => {
    if (counts.length === 0) return { min: 0, max: 1 };
    const min = Math.min(...counts);
    const max = Math.max(...counts);
    const padding = (max - min) * 0.5 || 1;
    return {
      min: Math.max(0, Math.floor(min - padding)),
      max: Math.ceil(max + padding),
    };
  }, [counts]);

  const showEmptyState = uniqueContactsQuery.isSuccess && counts.length === 0;
  const xAxis = useMemo<NonNullable<LineChartProps["xAxis"]>>(
    () => [
      {
        scaleType: "point",
        data: chartData.dates,
        height: 40,
        tickInterval: getContactsTickInterval(chartData.dates.length, mode),
        valueFormatter: (value: string, context) =>
          formatContactsChartDate(
            value,
            mode,
            context.location,
            canShowMonthly,
          ),
      },
    ],
    [chartData.dates, mode, canShowMonthly],
  );
  const yAxis = useMemo<NonNullable<LineChartProps["yAxis"]>>(
    () => [
      {
        min: yAxisRange.min,
        max: yAxisRange.max,
        valueFormatter: yAxisFormat.numberFormat,
      },
    ],
    [yAxisRange, yAxisFormat.numberFormat],
  );
  const series = useMemo<LineChartProps["series"]>(
    () => [
      {
        data: chartData.counts,
        label:
          mode === "monthly"
            ? t("billing.uniqueContacts.monthlyAverage", "Monthly average")
            : t("billing.uniqueContacts.title"),
        showMark: ({ index }) =>
          shouldShowContactsMark(chartData.counts, index, mode),
        curve: "linear",
        connectNulls: false,
        valueFormatter: valueFormat.numberFormat,
      },
    ],
    [chartData, mode, t, valueFormat.numberFormat],
  );

  return (
    <Card variant="outlined" sx={styles.card}>
      {uniqueContactsQuery.isPlaceholderData && (
        <LinearProgress sx={styles.refreshProgress} />
      )}
      <CardContent
        sx={styles.cardContent}
        aria-busy={
          uniqueContactsQuery.isPending || uniqueContactsQuery.isPlaceholderData
        }
      >
        <Box sx={styles.header}>
          <Box>
            <Stack direction="row" alignItems="center">
              <Typography variant="h2">
                {t("billing.uniqueContacts.title")}
              </Typography>
              <ContactsInfoTooltip
                dataAvailableFrom={
                  uniqueContactsQuery.data?.dataAvailableFrom ?? null
                }
              />
            </Stack>
            <ContactsFreshness
              lastSuccessfulSyncAt={
                uniqueContactsQuery.data?.lastSuccessfulSyncAt ?? null
              }
              dataThroughDate={
                uniqueContactsQuery.data?.dataThroughDate ?? null
              }
              isPending={uniqueContactsQuery.isPending}
            />
          </Box>
          <Box sx={styles.filters}>
            <Box sx={styles.controls}>
              {canShowMonthly && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={mode}
                  onChange={handleModeChange}
                  aria-label={t(
                    "billing.uniqueContacts.displayMode",
                    "Display mode",
                  )}
                >
                  <ToggleButton value="daily">
                    {t("billing.uniqueContacts.dailyValues", "Daily values")}
                  </ToggleButton>
                  <ToggleButton value="monthly">
                    {t(
                      "billing.uniqueContacts.monthlyAverage",
                      "Monthly average",
                    )}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <Select
                size="small"
                value={preset}
                onChange={handlePresetChange}
                inputProps={rangeInputProps}
              >
                {CONTACTS_RANGE_PRESETS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {presetLabels[value]}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={styles.range}>
              <Typography variant="note" color={theme.colors.staticFgBody}>
                {dateRange.reportingFrom.replaceAll("-", "/")} –{" "}
                {dateRange.reportingTo.replaceAll("-", "/")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {uniqueContactsQuery.isPending ? (
          <Stack alignItems="center" py="48px">
            <CircularProgress />
          </Stack>
        ) : uniqueContactsQuery.isError ? (
          <Box sx={styles.emptyState}>
            <Typography variant="body" color="textSecondary">
              {t("billing.error.generic")}
            </Typography>
          </Box>
        ) : uniqueContactsQuery.isPlaceholderData ? (
          <Box sx={styles.emptyState} />
        ) : showEmptyState ? (
          <Box sx={styles.emptyState}>
            <Typography variant="body" color="textSecondary">
              {t("billing.uniqueContacts.noData")}
            </Typography>
          </Box>
        ) : (
          <LineChart
            xAxis={xAxis}
            yAxis={yAxis}
            series={series}
            height={300}
            margin={chartMargin}
            slots={chartSlots}
          />
        )}
      </CardContent>
    </Card>
  );
};
